import React, { useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import faculty from '../data/faculty';

const GROUPS = [
  { key: 'administration', label: 'Administration' },
  { key: 'office',         label: 'Office Staff' },
  { key: 'related',        label: 'Related Services' },
  { key: 'enl',            label: 'English as a New Language' },
  { key: 'counselors',     label: 'School Counselors' },
  { key: 'elementary',     label: 'Elementary School Teachers' },
  { key: 'middle',         label: 'Middle School Teachers' },
  { key: 'cluster',        label: 'Cluster Teachers' },
];

const getInitials = (name) =>
  name
    .replace(/&/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const AVATAR_COLORS = [
  ['#00213a', '#fdbb2e'],
  ['#003a5d', '#fdbb2e'],
  ['#00213a', '#fff'],
  ['#664500', '#fdbb2e'],
  ['#1a3a2a', '#fdbb2e'],
  ['#2c1a4e', '#fdbb2e'],
];

const avatarStyle = (id) => {
  const idx = parseInt(id.replace('f', ''), 10) % AVATAR_COLORS.length;
  const [bg, fg] = AVATAR_COLORS[idx];
  return { backgroundColor: bg, color: fg };
};

const PersonCard = ({ person }) => (
  <div className="flex items-center gap-4 py-5">
    <div
      className="w-16 h-16 rounded-full flex-shrink-0 flex items-center justify-center text-base font-black shadow-sm select-none"
      style={avatarStyle(person.id)}
      aria-hidden="true"
    >
      {getInitials(person.name)}
    </div>
    <div className="min-w-0">
      {person.email ? (
        <a
          href={`mailto:${person.email}`}
          className="block font-semibold text-gray-900 leading-snug hover:text-[#00213a] transition-colors duration-150 focus:outline-none focus-visible:underline"
          aria-label={`Email ${person.name}`}
        >
          {person.name}
        </a>
      ) : (
        <p className="font-semibold text-gray-900 leading-snug">{person.name}</p>
      )}
      <p className="text-sm text-gray-500 mt-0.5 leading-snug">{person.role}</p>
    </div>
  </div>
);

const SectionDivider = ({ label }) => (
  <div className="flex items-center gap-5 my-10" role="separator">
    <div className="flex-1 h-px bg-gray-300" />
    <span className="text-xs font-black tracking-[0.18em] text-gray-500 uppercase whitespace-nowrap">
      {label}
    </span>
    <div className="flex-1 h-px bg-gray-300" />
  </div>
);

const FacultyDirectoryPage = () => {
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();

  const groupedFiltered = useMemo(() => {
    return GROUPS.map((g) => ({
      ...g,
      members: faculty.filter(
        (p) =>
          p.group === g.key &&
          (q ? `${p.name} ${p.role} ${p.email || ''}`.toLowerCase().includes(q) : true)
      ),
    })).filter((g) => g.members.length > 0);
  }, [q]);

  const totalVisible = groupedFiltered.reduce((sum, g) => sum + g.members.length, 0);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main id="main-content" className="pt-0">

        {/* Hero */}
        <section
          className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
        >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

          <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                <span className="text-sm font-semibold text-white tracking-tight">
                  Our Team
                </span>
              </div>
            </div>
            <h1
              className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight"
              style={{ letterSpacing: '-0.02em' }}
            >
              Faculty &amp; Staff
            </h1>
            <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
              Meet the educators, administrators, and staff who make PS/MS 96 a vibrant place to learn.
            </p>
          </div>
        </section>

        {/* Directory */}
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-24 mt-12">

        {/* Search */}
        <div className="flex items-center gap-3 mb-2">
          <label htmlFor="faculty-search" className="text-sm font-semibold text-gray-700 whitespace-nowrap">
            Search:
          </label>
          <input
            id="faculty-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="by name or position"
            className="w-56 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#00213a]/40 focus:border-[#00213a]"
          />
        </div>

        {/* Results */}
        {groupedFiltered.length === 0 ? (
          <p className="mt-16 text-center text-gray-500" role="status">
            No staff found matching &ldquo;{query}&rdquo;.
          </p>
        ) : (
          <>
            {q && (
              <p className="mt-3 text-xs text-gray-600" role="status">
                {totalVisible} result{totalVisible !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;
              </p>
            )}

            {groupedFiltered.map((group, gi) => (
              <section key={group.key} aria-labelledby={`section-${group.key}`}>
                <SectionDivider label={group.label} />
                <h2 id={`section-${group.key}`} className="sr-only">
                  {group.label}
                </h2>
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 list-none p-0 divide-y divide-gray-100 sm:divide-y-0">
                  {group.members.map((person) => (
                    <li
                      key={person.id}
                      className="border-b border-gray-100 sm:border-b-0 sm:border-r last:border-r-0 border-gray-100 px-2"
                    >
                      <PersonCard person={person} />
                    </li>
                  ))}
                </ul>
                {gi < groupedFiltered.length - 1 && (
                  <div className="mt-4 border-b border-gray-200" />
                )}
              </section>
            ))}
          </>
        )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FacultyDirectoryPage;
